import { useState } from 'react';
import { Plus, Trash2, Building2, X } from 'lucide-react';
import { useDepartments, useCreateDepartment, useDeleteDepartment } from '@/api/queries/employee.queries';
import { PageHeader } from '@/components/shared/PageHeader';
import { Button } from '@/components/ui/button';

function AddDepartmentModal({ onClose }) {
  const createDept = useCreateDepartment();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const inputCls = "w-full h-10 px-3 bg-[var(--bg-muted)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Department name is required'); return; }
    try {
      await createDept.mutateAsync({ name: name.trim(), description: description.trim() || undefined });
      onClose();
    } catch (err) {
      setError(err?.response?.data?.error?.message || 'Failed to create department.');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-[var(--bg-card)] rounded-[20px] shadow-[var(--shadow-xl)] w-full max-w-sm border border-[var(--border)] fade-in">
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-[var(--border)]">
          <h2 className="text-[18px] font-bold text-[var(--text-primary)]">New Department</h2>
          <button onClick={onClose} className="p-2 rounded-[8px] hover:bg-[var(--bg-muted)] text-[var(--text-secondary)]"><X size={16}/></button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">
              Department Name <span className="text-[var(--danger)]">*</span>
            </label>
            <input
              value={name}
              onChange={e => { setName(e.target.value); setError(''); }}
              placeholder="e.g. Engineering, Sales, HR"
              className={inputCls}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[13px] font-semibold text-[var(--text-primary)]">Description <span className="text-[11px] font-normal text-[var(--text-tertiary)]">(optional)</span></label>
            <input
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Short description of this department"
              className={inputCls}
            />
          </div>
          {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
          <div className="flex gap-2 pt-1">
            <Button type="submit" loading={createDept.isPending} className="flex-1">Create Department</Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DepartmentsPage() {
  const { data: deptsResp, isLoading } = useDepartments();
  const deleteDept = useDeleteDepartment();
  const departments = deptsResp?.data || [];
  const [showAdd, setShowAdd] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const handleDelete = async (id, name) => {
    if (!confirm(`Delete department "${name}"? Employees in this department will have their department cleared.`)) return;
    setDeletingId(id);
    try {
      await deleteDept.mutateAsync(id);
    } catch (err) {
      alert(err?.response?.data?.error?.message || 'Failed to delete department.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-5 fade-in max-w-xl">
      <PageHeader
        title="Departments"
        subtitle={`${departments.length} department${departments.length !== 1 ? 's' : ''}`}
        breadcrumb={[{ label: 'Settings', href: '/settings' }, { label: 'Departments' }]}
        actions={<Button size="sm" icon={Plus} onClick={() => setShowAdd(true)}>Add Department</Button>}
      />

      {isLoading ? (
        <div className="card text-center py-10 text-[14px] text-[var(--text-tertiary)]">Loading…</div>
      ) : departments.length === 0 ? (
        <div className="card text-center py-12 space-y-3">
          <div className="w-12 h-12 rounded-full bg-[var(--bg-muted)] flex items-center justify-center mx-auto">
            <Building2 size={22} className="text-[var(--text-tertiary)]"/>
          </div>
          <div>
            <p className="text-[15px] font-semibold text-[var(--text-primary)]">No departments yet</p>
            <p className="text-[13px] text-[var(--text-secondary)] mt-1">Add your first department to start organizing employees.</p>
          </div>
          <Button size="sm" icon={Plus} onClick={() => setShowAdd(true)}>Add First Department</Button>
        </div>
      ) : (
        <div className="card p-0 overflow-hidden divide-y divide-[var(--border)]">
          {departments.map(d => (
            <div key={d.id} className="flex items-center justify-between px-5 py-3.5 hover:bg-[var(--bg-muted)] transition-colors">
              <div>
                <p className="text-[14px] font-semibold text-[var(--text-primary)]">{d.name}</p>
                {d.description && <p className="text-[12px] text-[var(--text-tertiary)] mt-0.5">{d.description}</p>}
              </div>
              <button
                onClick={() => handleDelete(d.id, d.name)}
                disabled={deletingId === d.id}
                className="p-2 rounded-[6px] hover:bg-[var(--danger)]/10 text-[var(--text-tertiary)] hover:text-[var(--danger)] transition-colors disabled:opacity-50"
              >
                <Trash2 size={14}/>
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-[12px] text-[var(--text-tertiary)]">
        Departments are used to organize employees. You can assign departments when adding or editing an employee.
      </p>

      {showAdd && <AddDepartmentModal onClose={() => setShowAdd(false)} />}
    </div>
  );
}
